import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  TasksService,
  type TaskCreateBodyParams,
  type TaskCreateResponse,
  type TaskUpdateBodyParams,
  type TaskUpdateResponse,
  type TaskDeleteResponse,
} from 'src/api/generated';
import { updateCache } from 'src/modules/shared/utils/cache/update.util';
import type { CacheKeyValue } from 'src/types/cache';

/** Minimal task identity used by mutation cache updates. */
interface TaskModelWithId {
  /** Stable task identifier. */
  id: string;
}

const doNothing = (): void => undefined;

const hasTaskId = (task: { id?: string }): task is TaskModelWithId => {
  return typeof task.id === 'string' && task.id.length > 0;
};

interface UseCreateTaskMutationResponse {
  createTask: (data: TaskCreateBodyParams) => Promise<TaskCreateResponse>;
  loading: boolean;
  error: Error | null;
}

export const useCreateTaskMutation = (
  onSuccess: (newTask: TaskCreateResponse) => void = doNothing,
  onError: (error: Error) => void = doNothing
): UseCreateTaskMutationResponse => {
  const queryClient = useQueryClient();

  const createTask = async (
    data: TaskCreateBodyParams
  ): Promise<TaskCreateResponse> => {
    return await TasksService.createTask(data);
  };

  const {
    mutateAsync,
    isPending: loading,
    error,
  } = useMutation({
    mutationFn: createTask,
    onSuccess: async (newTask) => {
      const queryKeys: CacheKeyValue[][] = [
        ['tasks'],
        ['agenda-tasks-list'],
        ['agenda-tasks-calendar'],
      ];

      if (!hasTaskId(newTask)) {
        await queryClient.invalidateQueries({ queryKey: ['tasks'] });
        await queryClient.invalidateQueries({
          queryKey: ['agenda-tasks-list'],
        });
        await queryClient.invalidateQueries({
          queryKey: ['agenda-tasks-calendar'],
        });
        onSuccess(newTask);
        return;
      }

      queryKeys.forEach((queryKey) => {
        updateCache<TaskModelWithId>({
          queryClient,
          queryKey,
          updatedData: newTask,
          exact: false,
        });
      });

      onSuccess(newTask);
    },
    onError: (error: Error) => onError(error),
  });

  return {
    createTask: mutateAsync,
    loading,
    error,
  };
};

interface UpdateTaskParams {
  taskId: string;
  data: TaskUpdateBodyParams;
}

interface UseUpdateTaskMutationResponse {
  updateTask: (params: UpdateTaskParams) => Promise<TaskUpdateResponse>;
  loading: boolean;
  error: Error | null;
}

export const useUpdateTaskMutation = (
  onSuccess: (updatedTask: TaskUpdateResponse) => void = doNothing,
  onError: (error: Error) => void = doNothing
): UseUpdateTaskMutationResponse => {
  const queryClient = useQueryClient();

  const updateTask = async (
    params: UpdateTaskParams
  ): Promise<TaskUpdateResponse> => {
    return await TasksService.updateTask(params.taskId, params.data);
  };

  const {
    mutateAsync,
    isPending: loading,
    error,
  } = useMutation({
    mutationFn: updateTask,
    onSuccess: async (updatedTask) => {
      const queryKeys: CacheKeyValue[][] = [
        ['tasks'],
        ['agenda-tasks-list'],
        ['agenda-tasks-calendar'],
      ];

      if (!hasTaskId(updatedTask)) {
        await queryClient.invalidateQueries({ queryKey: ['tasks'] });
        await queryClient.invalidateQueries({
          queryKey: ['agenda-tasks-list'],
        });
        await queryClient.invalidateQueries({
          queryKey: ['agenda-tasks-calendar'],
        });
        onSuccess(updatedTask);
        return;
      }

      queryKeys.forEach((queryKey) => {
        updateCache<TaskModelWithId>({
          queryClient,
          queryKey,
          updatedData: updatedTask,
          exact: false,
        });
      });

      onSuccess(updatedTask);
    },
    onError: (error: Error) => onError(error),
  });

  return {
    updateTask: mutateAsync,
    loading,
    error,
  };
};

interface UseDeleteTaskMutationResponse {
  deleteTask: (taskId: string) => Promise<TaskDeleteResponse>;
  loading: boolean;
  error: Error | null;
}

export const useDeleteTaskMutation = (
  onSuccess: (deletedTask: TaskDeleteResponse) => void = doNothing,
  onError: (error: Error) => void = doNothing
): UseDeleteTaskMutationResponse => {
  const queryClient = useQueryClient();

  const deleteTask = async (taskId: string): Promise<TaskDeleteResponse> => {
    return await TasksService.deleteTask(taskId);
  };

  const {
    mutateAsync,
    isPending: loading,
    error,
  } = useMutation({
    mutationFn: deleteTask,
    onSuccess: async (deletedTask) => {
      const queryKeys: CacheKeyValue[][] = [
        ['tasks'],
        ['agenda-tasks-list'],
        ['agenda-tasks-calendar'],
      ];

      if (!hasTaskId(deletedTask)) {
        await queryClient.invalidateQueries({ queryKey: ['tasks'] });
        await queryClient.invalidateQueries({
          queryKey: ['agenda-tasks-list'],
        });
        await queryClient.invalidateQueries({
          queryKey: ['agenda-tasks-calendar'],
        });
        onSuccess(deletedTask);
        return;
      }

      queryKeys.forEach((queryKey) => {
        updateCache<TaskModelWithId>({
          queryClient,
          queryKey,
          updatedData: deletedTask,
          shouldRemoveItem: (_matchedKey, oldItem) =>
            oldItem.id.toString() === deletedTask.id.toString(),
          exact: false,
        });
      });

      onSuccess(deletedTask);
    },
    onError: (error: Error) => onError(error),
  });

  return {
    deleteTask: mutateAsync,
    loading,
    error,
  };
};
